import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";

const BACKEND_URL = "https://reflex-backend.alfaoir1.workers.dev";

export default function BusinessProfile() {
  const { name } = useParams();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    fetch(`${BACKEND_URL}/public-profile/${encodeURIComponent(name)}`)
      .then(r => r.json())
      .then(data => {
        if (data.success) setProfile(data.profile);
        else setError("Profile not found");
      })
      .catch(() => setError("Server error"))
      .finally(() => setLoading(false));
  }, [name]);

  if (loading) return (
    <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh", background: "#fafafa" }}>
      <p style={{ color: "#888", fontSize: "15px" }}>Loading profile...</p>
    </div>
  );

  if (error) return (
    <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh", background: "#fafafa" }}>
      <p style={{ color: "#999", fontSize: "15px" }}>{error}</p>
    </div>
  );

  const services = profile.services
    ? profile.services.split("\n").filter(s => s.trim())
    : [];

  return (
    <div style={{ background: "#fafafa", minHeight: "100vh", fontFamily: "'SF Pro Display', -apple-system, sans-serif" }}>
      <style>{`
        .social-btn:hover { background: #1a1a1a !important; color: #fff !important; }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }
        .fade-in { animation: fadeIn 0.4s ease forwards; }
      `}</style>

      {/* HERO */}
      <div style={{ background: "linear-gradient(135deg, #1a1a2e 0%, #2d1b4e 100%)", padding: "80px 24px 60px" }}>
        <div style={{ maxWidth: "800px", margin: "0 auto", display: "flex", alignItems: "center", gap: "28px", flexWrap: "wrap" }}>
          {/* Avatar */}
          {profile.profilePicture ? (
            <img src={profile.profilePicture} alt={profile.name}
              style={{ width: "110px", height: "110px", borderRadius: "50%", objectFit: "cover", border: "3px solid rgba(255,255,255,0.2)" }} />
          ) : (
            <div style={{ width: "110px", height: "110px", borderRadius: "50%", background: "rgba(255,255,255,0.1)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "40px", fontWeight: 700, color: "#fff", border: "3px solid rgba(255,255,255,0.2)", flexShrink: 0 }}>
              {(profile.businessName || profile.name || "?").charAt(0).toUpperCase()}
            </div>
          )}

          <div style={{ flex: 1 }}>
            <h1 style={{ color: "#fff", margin: "0 0 6px", fontSize: "28px", fontWeight: 700, letterSpacing: "-0.5px" }}>
              {profile.businessName || profile.name}
            </h1>
            <p style={{ color: "rgba(255,255,255,0.6)", margin: "0 0 12px", fontSize: "15px" }}>{profile.name}</p>
            {profile.serviceArea && (
              <span style={{ display: "inline-flex", alignItems: "center", gap: "5px", color: "rgba(255,255,255,0.5)", fontSize: "13px" }}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/>
                </svg>
                {profile.serviceArea}
              </span>
            )}
          </div>
        </div>
      </div>

      <div style={{ maxWidth: "800px", margin: "0 auto", padding: "32px 20px 60px" }}>

        {/* ABOUT */}
        {profile.about && (
          <div style={cardStyle} className="fade-in">
            <h3 style={sectionTitle}>About</h3>
            <p style={{ color: "#444", lineHeight: 1.75, fontSize: "14px", margin: 0 }}>{profile.about}</p>
          </div>
        )}

        {/* BUSINESS DETAILS */}
        <div style={cardStyle} className="fade-in">
          <h3 style={sectionTitle}>Business Details</h3>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px" }}>
            {profile.companyName && <Detail label="Company" value={profile.companyName} />}
            {profile.serviceArea && <Detail label="Service Area" value={profile.serviceArea} />}
            {profile.truckCount && <Detail label="Trucks" value={profile.truckCount} />}
            {profile.teamSize && <Detail label="Team Size" value={profile.teamSize} />}
            {profile.phone && <Detail label="Phone" value={profile.phone} />}
          </div>
        </div>

        {/* SERVICES */}
        {services.length > 0 && (
          <div style={cardStyle} className="fade-in">
            <h3 style={sectionTitle}>Services</h3>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
              {services.map((s, i) => (
                <span key={i} style={{ background: "#f0f0f0", color: "#1a1a1a", padding: "6px 14px", borderRadius: "20px", fontSize: "13px", fontWeight: 500 }}>
                  {s.trim()}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* SOCIAL LINKS */}
        {(profile.website || profile.facebook || profile.instagram || profile.linkedin || profile.googleMaps) && (
          <div style={cardStyle} className="fade-in">
            <h3 style={sectionTitle}>Links & Social</h3>
            <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
              {profile.website && <SocialBtn href={profile.website} label="Website" icon={<WebIcon />} />}
              {profile.facebook && <SocialBtn href={profile.facebook} label="Facebook" icon={<FbIcon />} />}
              {profile.instagram && <SocialBtn href={profile.instagram} label="Instagram" icon={<IgIcon />} />}
              {profile.linkedin && <SocialBtn href={profile.linkedin} label="LinkedIn" icon={<LiIcon />} />}
              {profile.googleMaps && <SocialBtn href={profile.googleMaps} label="Google Maps" icon={<MapIcon />} />}
            </div>
          </div>
        )}

        {/* GALLERY */}
        {((profile.photos && profile.photos.length > 0) || (profile.videos && profile.videos.length > 0)) && (
          <div style={cardStyle} className="fade-in">
            <h3 style={sectionTitle}>Gallery</h3>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: "12px" }}>
              {(profile.photos || []).filter(Boolean).map((url, i) => (
                <div key={`p-${i}`} style={{ borderRadius: "10px", overflow: "hidden", aspectRatio: "4/3" }}>
                  <img src={url} alt={`photo-${i}`} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                </div>
              ))}
              {(profile.videos || []).filter(Boolean).map((url, i) => (
                <div key={`v-${i}`} style={{ borderRadius: "10px", overflow: "hidden" }}>
                  <video src={url} controls style={{ width: "100%", display: "block", borderRadius: "10px" }} />
                </div>
              ))}
            </div>
          </div>
        )}

      </div>
    </div>
  );
}

// ===== STYLES =====
const cardStyle = {
  background: "#fff",
  borderRadius: "16px",
  border: "1px solid #e8e8e8",
  padding: "28px",
  marginBottom: "16px",
};

const sectionTitle = {
  fontSize: "15px",
  fontWeight: 700,
  color: "#1a1a1a",
  marginBottom: "20px",
  paddingBottom: "12px",
  borderBottom: "1px solid #f0f0f0",
  letterSpacing: "-0.2px",
};

function Detail({ label, value }) {
  return (
    <div>
      <p style={{ margin: "0 0 3px", fontSize: "11px", color: "#999", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px" }}>{label}</p>
      <p style={{ margin: 0, fontSize: "14px", color: "#1a1a1a", fontWeight: 500 }}>{value}</p>
    </div>
  );
}

function SocialBtn({ href, label, icon }) {
  return (
    <a href={href} target="_blank" rel="noreferrer" className="social-btn"
      style={{ display: "inline-flex", alignItems: "center", gap: "7px", padding: "8px 14px", background: "#f5f5f5", color: "#1a1a1a", borderRadius: "8px", textDecoration: "none", fontSize: "13px", fontWeight: 500, border: "1px solid #e8e8e8", transition: "all 0.2s" }}>
      {icon} {label}
    </a>
  );
}

const WebIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/>
    <path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z"/>
  </svg>
);
const FbIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M18 2h-3a5 5 0 00-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 011-1h3z"/>
  </svg>
);
const IgIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="2" y="2" width="20" height="20" rx="5"/><circle cx="12" cy="12" r="4"/>
    <circle cx="17.5" cy="6.5" r="0.5" fill="currentColor"/>
  </svg>
);
const LiIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M16 8a6 6 0 016 6v7h-4v-7a2 2 0 00-2-2 2 2 0 00-2 2v7h-4v-7a6 6 0 016-6z"/>
    <rect x="2" y="9" width="4" height="12"/><circle cx="4" cy="4" r="2"/>
  </svg>
);
const MapIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/>
  </svg>
);